/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Coordinates due-date modal state across AssignmentListItem rows.
 *
 * @file AssignmentDueDateModalProvider.jsx
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';

const AssignmentDueDateModalContext = createContext(null);

export function AssignmentDueDateModalProvider({
  textInfo,
  onDueDateSave,
  children
}) {
  const [activeAssignmentId, setActiveAssignmentId] = useState(null);

  const openDueDateModal = useCallback((assignment) => {
    setActiveAssignmentId(assignment?.id ?? null);
  }, []);

  const closeDueDateModal = useCallback(() => {
    setActiveAssignmentId(null);
  }, []);

  const handleDone = useCallback((assignment, date) => {
    onDueDateSave?.(assignment, date);
    setActiveAssignmentId(null);
  }, [onDueDateSave]);

  const value = useMemo(
    () => ({
      activeAssignmentId,
      openDueDateModal,
      closeDueDateModal,
      handleDone,
      textInfo
    }),
    [activeAssignmentId, openDueDateModal, closeDueDateModal, handleDone, textInfo]
  );

  return (
    <AssignmentDueDateModalContext.Provider value={value}>
      {children}
    </AssignmentDueDateModalContext.Provider>
  );
}

AssignmentDueDateModalProvider.propTypes = {
  textInfo: PropTypes.object.isRequired,
  onDueDateSave: PropTypes.func,
  children: PropTypes.node.isRequired
};

AssignmentDueDateModalProvider.defaultProps = {
  onDueDateSave: null
};

export function useAssignmentDueDateModal() {
  return useContext(AssignmentDueDateModalContext);
}

export default AssignmentDueDateModalProvider;
